import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth0 } from '../../../auth/useAuth0';
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { useBusinessContext } from '../../../context/BusinessContext';
import { formatMoney } from '../../../util/money';
import {
    AppointmentsReport,
    Granularity,
    RevenueReport,
    ServiceRevenue,
    TeamMemberRevenue,
} from '../../../model/report/Report';
import {
    getAppointmentsReport,
    getRevenueByService,
    getRevenueByTeamMember,
    getRevenueReport,
} from '../../../service/report/report-service';
import { exportCsv } from '../../../util/export/csv';
import { exportPdf } from '../../../util/export/pdf';
import { exportXml } from '../../../util/export/xml';
import { getAppointmentsByBusinessAndRange } from '../../../service/appointment/appointment-service';
import { getBusinessHealthPlans } from '../../../service/business/health-plan-service';
import { getServicesByBusinessId } from '../../../service/service/service-service';
import { getAllClientsByBusinessId } from '../../../service/client/client-service';
import './ReportsComponent.css';

type Tab = 'revenue' | 'team' | 'service' | 'appointments' | 'convenios';
const TAB_KEYS: Tab[] = ['revenue', 'team', 'service', 'appointments', 'convenios'];

/** One convênio appointment row assembled client-side for the billing report. */
interface ConvenioRow {
    date: string;
    appointmentId: string;
    patient: string;
    planName: string;
    cardNumber: string;
    serviceName: string;
    /** Amount the plan pays (a receber) — the appointment's stored convênio value. */
    value: number;
    copay: number;
    status: string;
}

type Preset = 'LAST_7' | 'LAST_30' | 'THIS_MONTH' | 'LAST_MONTH' | 'THIS_YEAR' | 'CUSTOM';
const PRESET_KEYS: Preset[] = ['LAST_7', 'LAST_30', 'THIS_MONTH', 'LAST_MONTH', 'THIS_YEAR', 'CUSTOM'];

const ymd = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const presetRange = (p: Preset): { start: string; end: string } => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    switch (p) {
        case 'LAST_7': {
            const s = new Date(today);
            s.setDate(s.getDate() - 6);
            return { start: ymd(s), end: ymd(today) };
        }
        case 'LAST_30': {
            const s = new Date(today);
            s.setDate(s.getDate() - 29);
            return { start: ymd(s), end: ymd(today) };
        }
        case 'THIS_MONTH':
            return { start: ymd(new Date(now.getFullYear(), now.getMonth(), 1)), end: ymd(today) };
        case 'LAST_MONTH':
            return {
                start: ymd(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
                end: ymd(new Date(now.getFullYear(), now.getMonth(), 0)),
            };
        case 'THIS_YEAR':
            return { start: ymd(new Date(now.getFullYear(), 0, 1)), end: ymd(today) };
        default:
            return { start: ymd(today), end: ymd(today) };
    }
};

// These label functions are now i18n-aware and must be called inside components
// that have access to the t() function. They're defined as closures below.

/** Categorical palette for pie/legend slices — brand first, then distinct hues. */
const PALETTE = ['#7266e8', '#b566d4', '#0d6efd', '#198754', '#f59e0b', '#e63946', '#14b8a6', '#6c757d'];

const ReportsComponent: React.FC = () => {
    const { t } = useTranslation();
    const { getAccessTokenSilently } = useAuth0();
    const { selectedBusiness } = useBusinessContext();
    const businessId = selectedBusiness ? Number(selectedBusiness.id) : null;
    const currency = selectedBusiness?.currency;
    const brand = (selectedBusiness as any)?.brandColor || '#7266e8';

    const [tab, setTab] = useState<Tab>('revenue');
    const [preset, setPreset] = useState<Preset>('THIS_MONTH');
    const initial = presetRange('THIS_MONTH');
    const [start, setStart] = useState(initial.start);
    const [end, setEnd] = useState(initial.end);
    const [granularity, setGranularity] = useState<Granularity>('DAY');

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [revenue, setRevenue] = useState<RevenueReport | null>(null);
    const [team, setTeam] = useState<TeamMemberRevenue[]>([]);
    const [service, setService] = useState<ServiceRevenue[]>([]);
    const [appts, setAppts] = useState<AppointmentsReport | null>(null);
    const [convenioRows, setConvenioRows] = useState<ConvenioRow[]>([]);

    const money = useCallback((v: number) => formatMoney(v, currency), [currency]);
    const periodLabel = `${start} → ${end}`;
    const methodLabel = (m: string) => t('payments.method.' + m, { defaultValue: m });
    const statusLabel = (s: string) => t('reports.status.' + s, { defaultValue: s });

    const load = useCallback(async () => {
        if (!businessId) return;
        // Mirror the backend cap: a report window can span at most 2 years (custom
        // ranges only — presets never exceed a year). Avoids an OOM-prone request.
        const spanDays = (new Date(end).getTime() - new Date(start).getTime()) / 86_400_000;
        if (spanDays > 366 * 2) {
            setError(t('reports.errors.rangeTooLarge'));
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const token = await getAccessTokenSilently();
            if (tab === 'revenue') {
                setRevenue(await getRevenueReport(businessId, start, end, granularity, token));
            } else if (tab === 'team') {
                setTeam(await getRevenueByTeamMember(businessId, start, end, token));
            } else if (tab === 'service') {
                setService(await getRevenueByService(businessId, start, end, token));
            } else if (tab === 'convenios') {
                // Assembled client-side: convênio appointments in range + carteirinha
                // (client convênios) + plan/service names. No report-service change needed.
                const [appointments, plans, services, clients] = await Promise.all([
                    getAppointmentsByBusinessAndRange(String(businessId), `${start}T00:00:00`, `${end}T23:59:59`, token),
                    getBusinessHealthPlans(businessId, token),
                    getServicesByBusinessId(businessId, token),
                    getAllClientsByBusinessId(String(businessId), token),
                ]);
                const planName = new Map(plans.map((p) => [String(p.id), p.name]));
                const serviceName = new Map(services.map((s) => [String(s.id), s.name ?? '']));
                const clientById = new Map(clients.map((c) => [String(c.id), c]));
                const rows: ConvenioRow[] = appointments
                    .filter((a) => a.paymentType === 'CONVENIO')
                    .map((a) => {
                        const client = a.clientId ? clientById.get(String(a.clientId)) : undefined;
                        const card = (client?.convenios ?? []).find(
                            (cv) => String(cv.healthPlanId) === String(a.healthPlanId)
                        )?.cardNumber ?? '';
                        return {
                            date: (a.startTime ?? '').slice(0, 10),
                            appointmentId: String(a.id),
                            patient: a.customerName ?? '',
                            planName: a.healthPlanId ? (planName.get(String(a.healthPlanId)) ?? '—') : '—',
                            cardNumber: card,
                            serviceName: serviceName.get(String(a.serviceId)) ?? '',
                            value: a.price ?? 0,
                            copay: a.copayAmount ?? 0,
                            status: a.status,
                        };
                    })
                    .sort((x, y) => x.date.localeCompare(y.date) || x.patient.localeCompare(y.patient));
                setConvenioRows(rows);
            } else {
                setAppts(await getAppointmentsReport(businessId, start, end, granularity, token));
            }
        } catch (e) {
            setError(e instanceof Error ? e.message : t('reports.alerts.loadError'));
        } finally {
            setLoading(false);
        }
    }, [businessId, tab, start, end, granularity, getAccessTokenSilently]);

    useEffect(() => {
        load();
    }, [load]);

    /* ---------------------------- export ---------------------------------- */

    const fileBase = useMemo(() => `report-${tab}-${start}_${end}`, [tab, start, end]);
    const title = `${selectedBusiness?.name ?? t('reports.business')} — ${t('reports.tabs.' + tab)}`;

    const exportCurrent = (format: 'csv' | 'pdf') => {
        let columns: string[] = [];
        let rows: (string | number)[][] = [];
        let summary: { label: string; value: string }[] | undefined;

        if (tab === 'revenue' && revenue) {
            columns = ['Method', 'Gross', 'Transactions'];
            rows = revenue.byMethod.map((m) => [methodLabel(m.method), m.gross.toFixed(2), m.count]);
            summary = [
                { label: 'Gross', value: money(revenue.gross) },
                { label: 'Net', value: money(revenue.net) },
                { label: 'Refunded', value: money(revenue.refunded) },
                { label: 'Discounts', value: money(revenue.discountTotal) },
                { label: 'Transactions', value: String(revenue.count) },
                { label: 'Avg ticket', value: money(revenue.averageTicket) },
            ];
        } else if (tab === 'team') {
            columns = ['Team member', 'Appointments', 'Transactions', 'Gross', 'Net', 'Refunded'];
            rows = team.map((t) => [t.teamMemberName, t.appointments, t.transactions, t.gross.toFixed(2), t.net.toFixed(2), t.refunded.toFixed(2)]);
        } else if (tab === 'service') {
            columns = ['Service', 'Category', 'Count', 'Gross', 'Net', 'Avg ticket'];
            rows = service.map((s) => [s.serviceName, s.categoryName ?? '—', s.count, s.gross.toFixed(2), s.net.toFixed(2), s.averageTicket.toFixed(2)]);
        } else if (tab === 'appointments' && appts) {
            columns = ['Team member', 'Total', 'Completed', 'Cancelled', 'No-show'];
            rows = appts.byTeamMember.map((t) => [t.teamMemberName, t.total, t.completed, t.cancelled, t.noShow]);
            summary = [
                { label: 'Total appointments', value: String(appts.total) },
                { label: 'Cancellation rate', value: `${appts.cancellationRate}%` },
                { label: 'No-show rate', value: `${appts.noShowRate}%` },
            ];
        } else if (tab === 'convenios') {
            columns = ['Data', 'Agendamento', 'Paciente', 'Plano', 'Carteirinha', 'Servico', 'Valor', 'Copay', 'Status'];
            rows = convenioRows.map((r) => [
                r.date, r.appointmentId, r.patient, r.planName, r.cardNumber, r.serviceName,
                r.value.toFixed(2), r.copay.toFixed(2), statusLabel(r.status),
            ]);
            const totalValue = convenioRows.reduce((s, r) => s + r.value, 0);
            const totalCopay = convenioRows.reduce((s, r) => s + r.copay, 0);
            summary = [
                { label: 'Atendimentos', value: String(convenioRows.length) },
                { label: 'Total a receber', value: money(totalValue) },
                { label: 'Total coparticipação', value: money(totalCopay) },
            ];
        }

        if (!rows.length && !summary) return;

        if (format === 'csv') {
            exportCsv(
                fileBase,
                columns.map((c, i) => ({ header: c, value: (r: (string | number)[]) => r[i] })),
                rows
            );
        } else {
            exportPdf({ title, subtitle: periodLabel, brandColor: brand, columns, rows, summary, filename: fileBase });
        }
    };

    /** Export the convênio report as generic XML (portable — transform to the
     *  operadora's format later). Per-appointment record with billing attributes. */
    const exportConvenioXml = () => {
        if (!convenioRows.length) return;
        exportXml<ConvenioRow>(
            fileBase,
            'convenioReport',
            'appointment',
            [
                { attr: 'id', value: (r) => r.appointmentId },
                { attr: 'date', value: (r) => r.date },
                { attr: 'patient', value: (r) => r.patient },
                { attr: 'plan', value: (r) => r.planName },
                { attr: 'card', value: (r) => r.cardNumber },
                { attr: 'service', value: (r) => r.serviceName },
                { attr: 'value', value: (r) => r.value.toFixed(2) },
                { attr: 'copay', value: (r) => r.copay.toFixed(2) },
                { attr: 'status', value: (r) => r.status },
            ],
            convenioRows,
            { business: selectedBusiness?.name ?? '', start, end }
        );
    };

    const canExport =
        (tab === 'revenue' && !!revenue) ||
        (tab === 'team' && team.length > 0) ||
        (tab === 'service' && service.length > 0) ||
        (tab === 'appointments' && !!appts) ||
        (tab === 'convenios' && convenioRows.length > 0);

    /* ---------------------------- render ---------------------------------- */

    if (!businessId) {
        return <div className="reports-page"><p className="text-muted">{t('reports.selectBusiness')}</p></div>;
    }

    return (
        <div className="reports-page" data-cy="reports-page">
            {/* Toolbar: tabs + filters + export */}
            <div className="list-card reports-toolbar">
                <div className="reports-tabs">
                    {TAB_KEYS.map((key) => (
                        <button
                            key={key}
                            type="button"
                            className={`reports-tab ${tab === key ? 'is-active' : ''}`}
                            onClick={() => setTab(key)}
                            data-cy={`reports-tab-${key}`}
                        >
                            {t('reports.tabs.' + key)}
                        </button>
                    ))}
                </div>

                <div className="reports-filters">
                    <select
                        className="form-select"
                        value={preset}
                        data-cy="reports-filter-preset"
                        onChange={(e) => {
                            const p = e.target.value as Preset;
                            setPreset(p);
                            if (p !== 'CUSTOM') {
                                const r = presetRange(p);
                                setStart(r.start);
                                setEnd(r.end);
                            }
                        }}
                    >
                        {PRESET_KEYS.map((k) => (
                            <option key={k} value={k}>{t('reports.presets.' + k)}</option>
                        ))}
                    </select>

                    {preset === 'CUSTOM' && (
                        <>
                            <input type="date" className="form-control" value={start} onChange={(e) => setStart(e.target.value)} data-cy="reports-filter-start" />
                            <input type="date" className="form-control" value={end} onChange={(e) => setEnd(e.target.value)} data-cy="reports-filter-end" />
                        </>
                    )}

                    {(tab === 'revenue' || tab === 'appointments') && (
                        <select className="form-select" value={granularity} onChange={(e) => setGranularity(e.target.value as Granularity)} data-cy="reports-filter-granularity">
                            <option value="DAY">{t('reports.granularity.DAY')}</option>
                            <option value="WEEK">{t('reports.granularity.WEEK')}</option>
                            <option value="MONTH">{t('reports.granularity.MONTH')}</option>
                        </select>
                    )}

                    <div className="reports-export">
                        <button type="button" className="btn btn-outline-secondary btn-sm" disabled={!canExport} onClick={() => exportCurrent('csv')} data-cy="reports-export-csv">{t('reports.exportCsv')}</button>
                        {tab === 'convenios' && (
                            <button type="button" className="btn btn-outline-secondary btn-sm" disabled={!canExport} onClick={exportConvenioXml} data-cy="reports-export-xml">{t('reports.exportXml')}</button>
                        )}
                        <button type="button" className="btn btn-outline-secondary btn-sm" disabled={!canExport} onClick={() => exportCurrent('pdf')} data-cy="reports-export-pdf">{t('reports.exportPdf')}</button>
                    </div>
                </div>
            </div>

            {error && <div className="alert alert-danger">{error}</div>}
            {loading && <div className="list-card reports-loading">{t('common.loading')}</div>}

            {!loading && tab === 'revenue' && <RevenueTab data={revenue} money={money} brand={brand} methodLabel={methodLabel} />}
            {!loading && tab === 'team' && <TeamTab rows={team} money={money} brand={brand} />}
            {!loading && tab === 'service' && <ServiceTab rows={service} money={money} brand={brand} />}
            {!loading && tab === 'appointments' && <AppointmentsTab data={appts} brand={brand} statusLabel={statusLabel} />}
            {!loading && tab === 'convenios' && <ConveniosTab rows={convenioRows} money={money} statusLabel={statusLabel} />}
        </div>
    );
};

/* ------------------------------- convênios -------------------------------- */

const ConveniosTab: React.FC<{
    rows: ConvenioRow[];
    money: (v: number) => string;
    statusLabel: (s: string) => string;
}> = ({ rows, money, statusLabel }) => {
    const { t } = useTranslation();
    if (!rows.length) {
        return <div className="list-card reports-loading">{t('reports.convenios.empty')}</div>;
    }
    const totalValue = rows.reduce((s, r) => s + r.value, 0);
    const totalCopay = rows.reduce((s, r) => s + r.copay, 0);
    return (
        <div className="list-card">
            <div className="reports-stats" style={{ marginBottom: '0.75rem' }}>
                <Stat label={t('reports.convenios.count')} value={String(rows.length)} />
                <Stat label={t('reports.convenios.totalValue')} value={money(totalValue)} />
                <Stat label={t('reports.convenios.totalCopay')} value={money(totalCopay)} />
            </div>
            <div className="table-responsive">
                <table className="table table-sm reports-table">
                    <thead>
                        <tr>
                            <th>{t('reports.convenios.col.date')}</th>
                            <th>{t('reports.convenios.col.patient')}</th>
                            <th>{t('reports.convenios.col.plan')}</th>
                            <th>{t('reports.convenios.col.card')}</th>
                            <th>{t('reports.convenios.col.service')}</th>
                            <th className="text-end">{t('reports.convenios.col.value')}</th>
                            <th className="text-end">{t('reports.convenios.col.copay')}</th>
                            <th>{t('reports.convenios.col.status')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.appointmentId}>
                                <td>{r.date}</td>
                                <td>{r.patient}</td>
                                <td>{r.planName}</td>
                                <td>
                                    {r.cardNumber || (
                                        <small className="text-danger">{t('reports.convenios.noCard')}</small>
                                    )}
                                </td>
                                <td>{r.serviceName}</td>
                                <td className="text-end">{money(r.value)}</td>
                                <td className="text-end">{r.copay ? money(r.copay) : '—'}</td>
                                <td>{statusLabel(r.status)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

/* ------------------------------- shared ----------------------------------- */

const Stat: React.FC<{ label: string; value: string; tone?: 'neg' | 'pos'; dataCy?: string }> = ({ label, value, tone, dataCy }) => (
    <div className="reports-stat" data-cy={dataCy}>
        <div className="reports-stat__label">{label}</div>
        <div className={`reports-stat__value ${tone ? `reports-stat__value--${tone}` : ''}`}>{value}</div>
    </div>
);

const ChartCard: React.FC<{ title: string; children: React.ReactNode; className?: string }> = ({ title, children, className }) => (
    <div className={`list-card reports-chart-card ${className ?? ''}`}>
        <h3 className="reports-chart-title">{title}</h3>
        {children}
    </div>
);

const axisProps = { stroke: '#9aa', fontSize: 11, tickLine: false } as const;

/* ------------------------------- tabs ------------------------------------- */

const RevenueTab: React.FC<{ data: RevenueReport | null; money: (n: number) => string; brand: string; methodLabel: (m: string) => string }> = ({ data, money, brand, methodLabel }) => {
    const { t } = useTranslation();
    if (!data || data.count === 0) return <EmptyState />;
    const pieData = data.byMethod.map((m) => ({ name: methodLabel(m.method), value: m.gross }));
    return (
        <>
            <div className="reports-stats" data-cy="reports-revenue-stats">
                <Stat label={t('reports.stat.gross')} value={money(data.gross)} dataCy="reports-stat-gross" />
                <Stat label={t('reports.stat.net')} value={money(data.net)} tone="pos" dataCy="reports-stat-net" />
                <Stat label={t('reports.stat.refunded')} value={money(data.refunded)} tone="neg" dataCy="reports-stat-refunded" />
                <Stat label={t('reports.stat.discounts')} value={money(data.discountTotal)} dataCy="reports-stat-discounts" />
                <Stat label={t('reports.stat.transactions')} value={String(data.count)} dataCy="reports-stat-count" />
                <Stat label={t('reports.stat.avgTicket')} value={money(data.averageTicket)} dataCy="reports-stat-avg-ticket" />
            </div>

            <div className="reports-grid">
                <ChartCard title={t('reports.chart.revenueOverTime')} className="reports-grid__wide">
                    <ResponsiveContainer width="100%" height={280}>
                        <AreaChart data={data.series} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                            <defs>
                                <linearGradient id="revFill" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={brand} stopOpacity={0.35} />
                                    <stop offset="100%" stopColor={brand} stopOpacity={0.02} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#eef" vertical={false} />
                            <XAxis dataKey="period" {...axisProps} />
                            <YAxis {...axisProps} width={56} tickFormatter={(v) => money(v as number)} />
                            <Tooltip formatter={(v: any, n: any) => [money(Number(v)), n === 'gross' ? t('reports.stat.gross') : t('reports.stat.net')]} />
                            <Area type="monotone" dataKey="gross" stroke={brand} strokeWidth={2} fill="url(#revFill)" name={t('reports.stat.gross')} />
                            <Area type="monotone" dataKey="net" stroke="#198754" strokeWidth={1.5} fillOpacity={0} name={t('reports.stat.net')} />
                        </AreaChart>
                    </ResponsiveContainer>
                </ChartCard>

                <ChartCard title={t('reports.chart.byMethod')}>
                    <ResponsiveContainer width="100%" height={280}>
                        <PieChart>
                            <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>
                                {pieData.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                            </Pie>
                            <Tooltip formatter={(v: any) => money(Number(v))} />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                </ChartCard>
            </div>

            <div className="list-card">
                <h3 className="reports-chart-title">{t('reports.chart.byMethod')}</h3>
                <table className="reports-table">
                    <thead><tr><th>{t('reports.col.method')}</th><th className="num">{t('reports.stat.gross')}</th><th className="num">{t('reports.stat.transactions')}</th></tr></thead>
                    <tbody>
                        {data.byMethod.map((m) => (
                            <tr key={m.method} data-cy={`reports-method-row-${m.method.toLowerCase()}`}><td>{methodLabel(m.method)}</td><td className="num">{money(m.gross)}</td><td className="num">{m.count}</td></tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
};

const TeamTab: React.FC<{ rows: TeamMemberRevenue[]; money: (n: number) => string; brand: string }> = ({ rows, money, brand }) => {
    const { t } = useTranslation();
    if (!rows.length) return <EmptyState />;
    const chart = rows.slice(0, 8).map((row) => ({ name: row.teamMemberName, gross: row.gross }));
    return (
        <>
            <ChartCard title={t('reports.chart.grossByTeam')}>
                <ResponsiveContainer width="100%" height={Math.max(160, chart.length * 38)}>
                    <BarChart data={chart} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#eef" horizontal={false} />
                        <XAxis type="number" {...axisProps} tickFormatter={(v) => money(v as number)} />
                        <YAxis type="category" dataKey="name" {...axisProps} width={140} />
                        <Tooltip formatter={(v: any) => money(Number(v))} />
                        <Bar dataKey="gross" fill={brand} radius={[0, 6, 6, 0]} name={t('reports.stat.gross')} />
                    </BarChart>
                </ResponsiveContainer>
            </ChartCard>

            <div className="list-card">
                <table className="reports-table">
                    <thead><tr><th>{t('reports.col.teamMember')}</th><th className="num">{t('reports.col.appts')}</th><th className="num">{t('reports.col.txns')}</th><th className="num">{t('reports.stat.gross')}</th><th className="num">{t('reports.stat.net')}</th><th className="num">{t('reports.stat.refunded')}</th></tr></thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr key={row.teamMemberId ?? row.teamMemberName} data-cy={`reports-team-row-${row.teamMemberId ?? row.teamMemberName}`}>
                                <td>{row.teamMemberName}</td>
                                <td className="num">{row.appointments}</td>
                                <td className="num">{row.transactions}</td>
                                <td className="num">{money(row.gross)}</td>
                                <td className="num">{money(row.net)}</td>
                                <td className="num">{money(row.refunded)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
};

const ServiceTab: React.FC<{ rows: ServiceRevenue[]; money: (n: number) => string; brand: string }> = ({ rows, money, brand }) => {
    const { t } = useTranslation();
    if (!rows.length) return <EmptyState />;
    const chart = rows.slice(0, 8).map((row) => ({ name: row.serviceName, gross: row.gross }));
    return (
        <>
            <ChartCard title={t('reports.chart.grossByService')}>
                <ResponsiveContainer width="100%" height={Math.max(160, chart.length * 38)}>
                    <BarChart data={chart} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#eef" horizontal={false} />
                        <XAxis type="number" {...axisProps} tickFormatter={(v) => money(v as number)} />
                        <YAxis type="category" dataKey="name" {...axisProps} width={140} />
                        <Tooltip formatter={(v: any) => money(Number(v))} />
                        <Bar dataKey="gross" fill={brand} radius={[0, 6, 6, 0]} name={t('reports.stat.gross')} />
                    </BarChart>
                </ResponsiveContainer>
            </ChartCard>

            <div className="list-card">
                <table className="reports-table">
                    <thead><tr><th>{t('reports.col.service')}</th><th>{t('reports.col.category')}</th><th className="num">{t('reports.col.count')}</th><th className="num">{t('reports.stat.gross')}</th><th className="num">{t('reports.stat.net')}</th><th className="num">{t('reports.stat.avgTicket')}</th></tr></thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr key={row.serviceId ?? row.serviceName} data-cy={`reports-service-row-${row.serviceId ?? row.serviceName}`}>
                                <td>{row.serviceName}</td>
                                <td>{row.categoryName ?? '—'}</td>
                                <td className="num">{row.count}</td>
                                <td className="num">{money(row.gross)}</td>
                                <td className="num">{money(row.net)}</td>
                                <td className="num">{money(row.averageTicket)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
};

const AppointmentsTab: React.FC<{ data: AppointmentsReport | null; brand: string; statusLabel: (s: string) => string }> = ({ data, brand, statusLabel }) => {
    const { t } = useTranslation();
    if (!data || data.total === 0) return <EmptyState />;
    const pieData = data.byStatus.map((row) => ({ name: statusLabel(row.status), value: row.count }));
    return (
        <>
            <div className="reports-stats" data-cy="reports-appointments-stats">
                <Stat label={t('reports.stat.totalAppts')} value={String(data.total)} dataCy="reports-stat-total-appts" />
                <Stat label={t('reports.stat.cancellationRate')} value={`${data.cancellationRate}%`} tone="neg" dataCy="reports-stat-cancellation-rate" />
                <Stat label={t('reports.stat.noShowRate')} value={`${data.noShowRate}%`} tone="neg" dataCy="reports-stat-noshow-rate" />
            </div>

            <div className="reports-grid">
                <ChartCard title={t('reports.chart.apptsOverTime')} className="reports-grid__wide">
                    <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={data.series} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#eef" vertical={false} />
                            <XAxis dataKey="period" {...axisProps} />
                            <YAxis {...axisProps} width={36} allowDecimals={false} />
                            <Tooltip />
                            <Bar dataKey="count" fill={brand} radius={[6, 6, 0, 0]} name={t('reports.tabs.appointments')} />
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>

                <ChartCard title={t('reports.chart.byStatus')}>
                    <ResponsiveContainer width="100%" height={280}>
                        <PieChart>
                            <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>
                                {pieData.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                            </Pie>
                            <Tooltip />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                </ChartCard>
            </div>

            <div className="list-card">
                <h3 className="reports-chart-title">{t('reports.chart.byTeamMember')}</h3>
                <table className="reports-table">
                    <thead><tr><th>{t('reports.col.teamMember')}</th><th className="num">{t('reports.col.total')}</th><th className="num">{t('reports.col.completed')}</th><th className="num">{t('reports.col.cancelled')}</th><th className="num">{t('reports.col.noShow')}</th></tr></thead>
                    <tbody>
                        {data.byTeamMember.map((row) => (
                            <tr key={row.teamMemberId ?? row.teamMemberName} data-cy={`reports-appt-team-row-${row.teamMemberId ?? row.teamMemberName}`}>
                                <td>{row.teamMemberName}</td>
                                <td className="num">{row.total}</td>
                                <td className="num">{row.completed}</td>
                                <td className="num">{row.cancelled}</td>
                                <td className="num">{row.noShow}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
};

const EmptyState: React.FC = () => {
    const { t } = useTranslation();
    return <div className="list-card reports-empty">{t('reports.empty')}</div>;
};

export default ReportsComponent;
